import {
    AddressItemType,
    deleteAutoForwarding,
    getAutoForwardingSummary,
    ipmQueryClient,
    PostageUsageType,
    updateAutoForwarding,
    useAutoForwarding
} from "@twigsby/client-data";
import {AutoForwardingSummaryResponseType} from "@twigsby/client-data/lib/Types/AutoForwardingTypes";
import {IpmRqKeys} from "@twigsby/client-data/lib/Wrappers/ReactQuery/CacheKeys";
import {
    AlertBanner,
    Button,
    ButtonSubmit,
    Card,
    CardLayoutType,
    ColourSolid,
    ComboBoxControl,
    ContentBlock,
    ContentLoader,
    FlexGap,
    StackVertical,
    ToggleSwitch,
    ToggleType,
    useTranslation
} from "@twigsby/frontend-framework";
import {Controller, FieldValues, FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import {FormatLocaleDate} from "@twigsby/helpers";
import React, {useEffect, useState} from "react";
import {useSite} from "../../../Providers/SiteInterfaceProvider";
import {AddressControl} from "../../AddressControl";
import {ErrorMapper} from "../../ErrorMapper";
import {PostageControl} from "../../PostageControl";
import AddTagControlMinimal from "../../Tags/AddTagControlMinimal";

const AutoForwarding = () => {
    const{ t } = useTranslation();

    const { currentLocale } = useSite();
    const { data, status, error } = useAutoForwarding();
    const [ responseError, setError ] = useState<any>(null);
    const [ showTagControl, setShowTagControl ] = useState<boolean>(data?.forwardingConditions.selectedValue === 1);

    const [ resetForm, setResetForm ] = useState<boolean>(false);

    const formMethods = useForm({
        defaultValues: {
            postageOptionId: data?.postageOptionId || -1,
            addressId: data?.addressId || -1,
            nextForwardingDate: data?.nextForwardingDate || new Date(),
            batchExistingItems: data?.batchExistingItems || false,
            forwardingConditions: data?.forwardingConditions.selectedValue || -1,
            forwardingFrequency: data?.forwardingFrequency.selectedValue || -1,
            forwardingConditionsValueId: data?.forwardingConditionsValueId || -1,
        },
        mode: 'onChange'
    });

    const [ summaryData, setSummaryData ] = useState<AutoForwardingSummaryResponseType | null>(null);
    const [ activeAddress, setActiveAddress] = useState<AddressItemType | null>(null);

    const GetSummary = () => {
        getAutoForwardingSummary().then((r) => {
            setSummaryData(r);
        })
    }

    const HandleDelete = () => {
        deleteAutoForwarding().then((r) => {
            ipmQueryClient.invalidateQueries([IpmRqKeys.ACCOUNT_SETTINGS_AUTO_FORWARDING_KEY]).then(() => {
                formMethods.reset();
                setResetForm(!resetForm);
                setShowTagControl(false);
            });
        })
    }

    const UpdateDetails = (data: any) => {

        updateAutoForwarding(data).then(r => setError(null)).catch((e) => setError(e))
    }

    useEffect(() => {
        const subscription = formMethods.watch((e) => {
            console.log('watch', e);
            console.log(formMethods.formState);
        });

        return () => subscription.unsubscribe();
    }, [formMethods.watch]);

    useEffect(() => {
        if(data && data.addressId) {
            GetSummary();
            setShowTagControl(data.forwardingConditions.selectedValue === 1);

            formMethods.setValue('addressId', data.addressId || -1);
            formMethods.setValue('postageOptionId', data.postageOptionId || -1);
            formMethods.setValue('nextForwardingDate', data.nextForwardingDate || new Date());
            formMethods.setValue('batchExistingItems', data.batchExistingItems || false);
            formMethods.setValue('forwardingConditions', data.forwardingConditions.selectedValue || -1);
            formMethods.setValue('forwardingFrequency', data.forwardingFrequency.selectedValue || -1);
            formMethods.setValue('forwardingConditionsValueId', data.forwardingConditionsValueId || -1);
        }
    }, [data]);

    return (
        <>
            {status === 'loading' ? (
                <ContentLoader childHeight={'6em'} childCount={6} />
            ) : status === "error" ? (
                <ContentBlock error={true} title={error.name} content={error.message} />
            ) : (
                <FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit((data: FieldValues) =>  UpdateDetails(data))}>
                        <fieldset>
                            <Card headerOptions={{ title: t('settings.sections.autoForwarding') }} type={CardLayoutType.SlimHeader} emptyBody={true} >

                                {summaryData && summaryData.nextForwardingDate && (
                                    <div className={'p-md'}>
                                        <AlertBanner title={t('settings.autoForwarding.labels.nextRun')} content={t('settings.autoForwarding.messages.nextRun', { count: summaryData.itemsInNextBatch, date: FormatLocaleDate(summaryData.nextForwardingDate, currentLocale) })} colourMod={ColourSolid.Primary} />
                                    </div>
                                )}

                                {responseError && (
                                    <div className={'p-md'}>
                                        <ErrorMapper title={responseError?.name} errorResponse={responseError} />
                                    </div>
                                )}

                                <div className={'user-action'}>
                                    <div className={'fade'}>{t('settings.autoForwarding.labels.destinationAddress')}</div>
                                    <div>
                                        <Controller
                                            control={formMethods.control}
                                            name={'addressId'}
                                            rules={{ min: 0 }}
                                            render={({ field }) =>
                                                <AddressControl
                                                    name={field.name}
                                                    showCreateAddressOptions={true}
                                                    singleSelection={true}
                                                    forwardItemCallback={(e) => {
                                                        setActiveAddress(e);
                                                        field.onChange(e.value);
                                                    }}
                                                    defaultValues={field.value !== -1 ? [field.value] : undefined}
                                                    minimal={true}
                                                    required={true}
                                                    reset={resetForm}
                                                />
                                            }
                                        />
                                    </div>
                                </div>
                                <div className={'user-action'}>
                                    <div className={'fade'}>{t('settings.autoForwarding.labels.postageOption')}</div>
                                    <div>
                                        <Controller
                                            control={formMethods.control}
                                            name={'postageOptionId'}
                                            rules={{ min: 0 }}
                                            render={({ field }) =>
                                                <PostageControl
                                                    postageType={PostageUsageType.Forwarding}
                                                    name={field.name}
                                                    activeAddress={activeAddress}
                                                    alwaysEnabled={true}
                                                    singleSelection={true}
                                                    forwardValueCallback={(e) => {
                                                        field.onChange(e);
                                                    }}
                                                    defaultValues={field.value !== -1  ? [field.value] : undefined}
                                                    minimal={true}
                                                    required={true}
                                                    reset={resetForm}
                                                />
                                            }
                                        />
                                    </div>
                                </div>
                                <div className={'user-action'}>
                                    <div className={'fade'}>{t('settings.autoForwarding.labels.forwardingConditions')}</div>
                                    <div>
                                        <Controller
                                            control={formMethods.control}
                                            name={'forwardingConditions'}
                                            rules={{ min: 0 }}
                                            render={({ field }) =>
                                                <ComboBoxControl
                                                    name={field.name}
                                                    placeholder={t('settings.autoForwarding.controls.forwardingConditionsPlaceholder')}
                                                    data={data?.forwardingConditions.options}
                                                    forwardValueCallback={(e) => {
                                                        setShowTagControl(e === 1);
                                                        field.onChange(e);
                                                    }}
                                                    valueType={'number'}
                                                    defaultValues={field.value !== -1  ? [field.value] : undefined}
                                                    singleSelection={true}
                                                    reset={resetForm}
                                                />
                                            }
                                        />
                                    </div>
                                </div>

                                {showTagControl && (
                                    <div className={'user-action'}>
                                        <div className={'fade'}>{t('settings.autoForwarding.labels.matchingTags')}</div>
                                        <div>
                                            <Controller
                                                control={formMethods.control}
                                                name={'forwardingConditionsValueId'}
                                                rules={{ min: 0 }}
                                                render={({ field }) =>
                                                    <AddTagControlMinimal
                                                        name={field.name}
                                                        minimal={true}
                                                        existingTags={field.value !== -1 ? [field.value] as Array<number>: undefined}
                                                        updateCallback={(e: any) => field.onChange(e)}
                                                        singleSelection={true}
                                                        reset={resetForm}
                                                    />
                                                }
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className={'user-action'}>
                                    <div className={'fade'}>{t('settings.autoForwarding.labels.nextForwardDate')}</div>
                                    <div>

{/*                                        <Controller
                                            control={formMethods.control}
                                            name={'nextForwardingDate'}
                                            render={({ field }) => {
                                                console.log('datepicker', field.value);
                                                return (
                                                    <DatePickerControl
                                                        singleDate={true}
                                                        locale={currentLocale}
                                                        defaultStartDate={field.value ? new Date(field.value) : new Date()}
                                                        forwardValueCallback={(e) => field.onChange(e)}
                                                        name={'nextForwardingDate'}
                                                    />
                                                )
                                            }
                                            }
                                        />*/}
                                    </div>
                                </div>
                                <div className={'user-action'}>
                                    <div className={'fade'}>{t('settings.autoForwarding.labels.forwardingFrequency')}</div>
                                    <div>
                                        <Controller
                                            control={formMethods.control}
                                            name={'forwardingFrequency'}
                                            rules={{ min: 0 }}
                                            render={({ field }) =>
                                                <ComboBoxControl
                                                    name={field.name}
                                                    placeholder={t('settings.autoForwarding.controls.forwardingFrequencyPlaceholder')}
                                                    data={data?.forwardingFrequency.options}
                                                    forwardValueCallback={(e) => {
                                                        field.onChange(e);
                                                    }}
                                                    valueType={'number'}
                                                    defaultValues={field.value !== -1  ? [field.value] : undefined}
                                                    singleSelection={true}
                                                    reset={resetForm}
                                                />
                                            }
                                        />
                                    </div>
                                </div>
                                <div className={'user-action'}>
                                    <div className={'fade'}>{t('settings.autoForwarding.labels.batchExistingItems')}</div>
                                    <div>
                                        <Controller
                                            control={formMethods.control}
                                            name={'batchExistingItems'}
                                            rules={{ min: 0 }}
                                            render={({ field }) =>
                                                <ToggleSwitch
                                                    name={field.name}
                                                    label={t('settings.autoForwarding.labels.batchExistingItems')}
                                                    type={ToggleType.Switch}
                                                    defaultChecked={field.value}
                                                    onChange={(e) => field.onChange( e.currentTarget.checked)}
                                                />
                                            }
                                        />
                                    </div>
                                </div>

                                <StackVertical gap={FlexGap.ExtraSmall} extraClass={'p-md'}>
                                    <ButtonSubmit title={t('settings.autoForwarding.actions.save')} fullWidth={true} colour={ColourSolid.Primary} loading={formMethods.formState.isSubmitting} disabled={formMethods.formState.isSubmitting || !formMethods.formState.isValid} />
                                    <Button title={t('settings.autoForwarding.actions.reset')} fullWidth={true} onClick={() => HandleDelete()} />
                                </StackVertical>
                            </Card>
                        </fieldset>
                    </form>
                </FormProvider>
            )}
        </>
    )
}

export { AutoForwarding }
